"use client";

import { useState, useEffect } from "react";
import { Bell, Info, CheckCircle2, AlertTriangle, XCircle, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { markNotificationRead, markAllNotificationsRead } from "@/query/notifications";
import type { Notification, NotificationType } from "@/lib/types";
import { useRouter } from "next/navigation";

const TYPE_CONFIG: Record<NotificationType, { icon: React.ElementType; color: string; border: string }> = {
  info: { icon: Info, color: "text-blue-500", border: "border-l-blue-500" },
  success: { icon: CheckCircle2, color: "text-green-500", border: "border-l-green-500" },
  warning: { icon: AlertTriangle, color: "text-amber-500", border: "border-l-amber-500" },
  error: { icon: XCircle, color: "text-red-500", border: "border-l-red-500" },
};

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (mins < 1) return "Gerade eben";
  if (mins < 60) return `Vor ${mins} Min.`;
  if (hours < 24) return `Vor ${hours} Std.`;
  return `Vor ${days} Tag${days === 1 ? "" : "en"}`;
}

type Props = {
  initialNotifications: Notification[];
  userId: string;
};

export function NotificationBell({ initialNotifications, userId }: Props) {
  const [notifications, setNotifications] = useState(initialNotifications);
  const [open, setOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const source = new EventSource("/api/notifications/stream");

    source.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload?.type === "balance_update") {
          window.dispatchEvent(new CustomEvent("balance-update"));
          return;
        }
        setNotifications((prev) => [payload as Notification, ...prev]);
      } catch {}
    };

    return () => source.close();
  }, [userId]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  function markRead(id: number) {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    markNotificationRead(id);
  }

  function markAllRead() {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    markAllNotificationsRead(userId);
  }

  function handleClick(n: Notification) {
    if (!n.is_read) markRead(n.id);
    if (n.link) {
      setOpen(false);
      router.push(n.link);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-white leading-none">{unreadCount > 9 ? "9+" : unreadCount}</span>}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">Benachrichtigungen</span>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <Check className="h-3 w-3" />
              Alle gelesen
            </button>
          )}
        </div>

        {/* List */}
        <ScrollArea className="max-h-[400px]">
          {notifications.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Keine Benachrichtigungen</p>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => {
                const { icon: Icon, color, border } = TYPE_CONFIG[n.type] ?? TYPE_CONFIG["info"];
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 border-l-2 ${border} transition-colors ${n.is_read ? "bg-background hover:bg-muted/40 opacity-60" : "bg-muted/30 hover:bg-muted/60"} ${n.link ? "cursor-pointer" : "cursor-default"}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-snug ${!n.is_read ? "font-semibold" : "font-medium"}`}>{n.title}</p>
                        {n.message && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>}
                        <p className="text-[11px] text-muted-foreground mt-1">{relativeTime(n.created_at)}</p>
                      </div>
                      {!n.is_read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
